import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type RefObject,
  type SetStateAction,
} from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { api } from '../../services/api';
import {
  incidentsApi,
  type Incident,
  type IncidentActivityItem,
  type IncidentComment,
  type IncidentOpsState,
  type IncidentReviewOutcome,
  type IncidentSeverity,
} from '../../services/incidentsApi';
import {
  reportsApi,
  type WorkflowClaimReadinessResponse,
  type WorkflowCorrectiveAction,
  type WorkflowCorrectiveActionEvidenceSummary,
  type WorkflowImpactSummary,
} from '../../services/reportsApi';
import { policiesApi } from '../../services/policiesApi';
import {
  isGroupedCorrectiveActionPlanDraftDirty,
  readPersistedCorrectiveAction,
} from '../../domain/incidents/groupedCorrectiveActionWorkflow';
import {
  buildControlReviewDraft,
  buildControlReviewPatch,
  readPersistedControlReview,
  type CorrectiveActionControlReviewDraft,
} from '../../domain/incidents/correctiveActionControlReview';
import {
  emptyFieldVerificationSummary,
  type FieldVerificationSummary,
} from '../../domain/incidents/fieldVerification';
import { persistIncidentReviewDecision } from './persistIncidentReviewDecision';
import { readFollowupOwnerLabel } from '../../domain/incidents/incidentOwnershipSemantics';
import { ownerRoleIsUnassigned } from '../../domain/incidents/incidentPatternVocabulary';
import { readImpactMeasurementWaived } from '../../domain/incidents/trackImpactSemantics';
import { displaySafetyManagerLabel } from '../../domain/safetyManagerUxLabels';
import {
  correctiveActionTypeLabel,
} from '../../domain/incidents/groupedCorrectiveActionPresentation';
import {
  buildBlockerSyncResultPayload,
  buildCoverageReviewValidationSync,
  buildVerificationMethodValidationSync,
  resolveOpenValidationTask,
  type GroupedBlockerSyncType,
} from '../../domain/incidents/groupedValidationTaskSync';
import {
  toIsoFromDateInput,
  toIsoFromLocalInput,
  toLocalDateInputValue,
  toLocalInputValue,
} from './useIncidentsTimeRange';

export type IncidentDrawerBusyState =
  | null
  | 'ack'
  | 'review'
  | 'close'
  | 'note'
  | 'severity'
  | 'suppress'
  | 'ensure'
  | 'sync'
  | 'ops'
  | 'ops-owner'
  | 'ops-corrective'
  | 'measurement'
  | 'export'
  | 'threshold';

type SnapshotAttachment = {
  filename: string;
  preferFileEndpoint: boolean;
};

type UseIncidentDrawerOperationsArgs = {
  selectedIncident: Incident | null;
  setSelectedIncident: Dispatch<SetStateAction<Incident | null>>;
  setIncidents: Dispatch<SetStateAction<Incident[]>>;
  edgeSuppressionsByDevice: Record<string, any[]>;
  setEdgeSuppressionsByDevice: Dispatch<SetStateAction<Record<string, any[]>>>;
  loadEdgeSuppressionsForDevices: (deviceIds: string[]) => Promise<void> | void;
  viewDialogOpen: boolean;
  setViewDialogTab: (tab: number) => void;
  fetchData: (opts?: { showLoading?: boolean; skipPersistedRefresh?: boolean }) => Promise<void>;
  setError: (message: string | null) => void;
  setIncidentComments: Dispatch<SetStateAction<IncidentComment[]>>;
  setIncidentCommentsError: (message: string | null) => void;
  setIncidentActivityItems: Dispatch<SetStateAction<IncidentActivityItem[]>>;
  commentInputRef: RefObject<HTMLInputElement | HTMLTextAreaElement | null>;
  navigate: NavigateFunction;
  getScopedOrgId: () => string;
  getScopedSiteId: () => string;
  fetchEvidenceFileBlob: (incidentId: string, filename: string) => Promise<Blob>;
  fetchLatestSnapshotBlob: (incidentId: string) => Promise<Blob>;
  fetchLatestVideoBlob: (incidentId: string) => Promise<Blob>;
  downloadBlob: (blob: Blob, filename: string) => void;
  bestSnapshotAttachment: (incident: Incident | null | undefined) => SnapshotAttachment | null;
  deriveOperationalStatus: (row: any) => string;
  groupedReviewMode?: boolean;
  resolveTargetIncidentId?: string;
  workflowId?: string;
  controlReviewPolicyAlias?: string;
};

function readControlReviewPolicyAlias(
  incident: Incident | null,
  explicitAlias?: string,
): string {
  const directAlias = String(explicitAlias || '').trim();
  if (directAlias) return directAlias;
  const row = (incident || {}) as any;
  const safetyCase = row?.safety_case;
  const scope = safetyCase?.scope && typeof safetyCase.scope === 'object' ? safetyCase.scope : {};
  const contexts = [
    row?.policy_report_context,
    row?.report_context,
    safetyCase?.policy_report_context,
    safetyCase?.report_context,
    scope?.policy_report_context,
    scope?.report_context,
  ];
  for (const context of contexts) {
    if (!context || typeof context !== 'object') continue;
    const alias = String((context as Record<string, unknown>).policy_alias || '').trim();
    if (alias) return alias;
  }
  return '';
}

export function useIncidentDrawerOperations({
  selectedIncident,
  setSelectedIncident,
  setIncidents,
  edgeSuppressionsByDevice,
  setEdgeSuppressionsByDevice,
  loadEdgeSuppressionsForDevices,
  viewDialogOpen,
  setViewDialogTab,
  fetchData,
  setError,
  setIncidentComments,
  setIncidentCommentsError,
  setIncidentActivityItems,
  commentInputRef,
  navigate,
  getScopedOrgId,
  getScopedSiteId,
  fetchEvidenceFileBlob,
  fetchLatestSnapshotBlob,
  fetchLatestVideoBlob,
  downloadBlob,
  bestSnapshotAttachment,
  deriveOperationalStatus,
  groupedReviewMode = false,
  resolveTargetIncidentId,
  workflowId = '',
  controlReviewPolicyAlias = '',
}: UseIncidentDrawerOperationsArgs) {
  const [drawerBusy, setDrawerBusy] = useState<IncidentDrawerBusyState>(null);
  const [suppressionsLoading, setSuppressionsLoading] = useState(false);
  const [suppressionsError, setSuppressionsError] = useState<string | null>(null);
  const [edgeSuppressions, setEdgeSuppressions] = useState<any[]>([]);
  const [suppressDialogOpen, setSuppressDialogOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeReviewOutcomePreset, setCloseReviewOutcomePreset] = useState<IncidentReviewOutcome | null>(null);
  const [closeReason, setCloseReason] = useState<'TRUE_POSITIVE' | 'FALSE_POSITIVE' | 'TEST' | 'DUPLICATE' | 'NOT_ACTIONABLE'>('TRUE_POSITIVE');
  const [closeNote, setCloseNote] = useState<string>('');
  const [thresholdDialogOpen, setThresholdDialogOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState<string>('');
  const [severityDraft, setSeverityDraft] = useState<IncidentSeverity>('medium');
  const [opsStateDraft, setOpsStateDraft] = useState<IncidentOpsState>('new');
  const [opsAssigneeDraft, setOpsAssigneeDraft] = useState<string>('');
  const [opsTeamDraft, setOpsTeamDraft] = useState<string>('');
  const [opsSlaTargetDraft, setOpsSlaTargetDraft] = useState<string>('');
  const [measurementBaselineDraft, setMeasurementBaselineDraft] = useState<string>('');
  const [measurementFollowUpDraft, setMeasurementFollowUpDraft] = useState<string>('');
  const [measurementMeasuredAtDraft, setMeasurementMeasuredAtDraft] = useState<string>('');
  const [measurementOutcomeStatusDraft, setMeasurementOutcomeStatusDraft] = useState<string>('');
  const [measurementNoteDraft, setMeasurementNoteDraft] = useState<string>('');
  const [measurementImpactWaivedDraft, setMeasurementImpactWaivedDraft] = useState(false);
  const [correctiveActionTypeDraft, setCorrectiveActionTypeDraftState] = useState('');
  const [correctiveActionPriorityDraft, setCorrectiveActionPriorityDraftState] = useState('');
  const [correctiveActionDueDateDraft, setCorrectiveActionDueDateDraftState] = useState('');
  const [correctiveActionVerificationMethodDraft, setCorrectiveActionVerificationMethodDraftState] = useState('');
  const emptyControlReviewDraft: CorrectiveActionControlReviewDraft = {
    controlObjective: '',
    controlLevel: '',
    potentialControlGap: '',
    fieldVerificationQuestion: '',
    verificationStatus: 'pending_field_verification',
    closureRequirement: '',
    followUpObservation: '',
    effectivenessBoundary: '',
  };
  const [correctiveActionControlReviewDraft, setCorrectiveActionControlReviewDraft] = useState(
    emptyControlReviewDraft,
  );
  const [ppeMissingZoneDiag, setPpeMissingZoneDiag] = useState<any | null>(null);
  const [ppeMissingZoneDiagLoading, setPpeMissingZoneDiagLoading] = useState(false);
  const [ppeMissingZoneDiagError, setPpeMissingZoneDiagError] = useState<string | null>(null);

  const applySelectedIncident = useCallback((fresh: Incident | null | undefined) => {
    if (!fresh?.incident_id) return;
    setSelectedIncident(fresh);
    setIncidents((prev) => (prev || []).map((row) => (row?.incident_id === fresh.incident_id ? fresh : row)));
  }, [setIncidents, setSelectedIncident]);

  const refreshSelectedIncident = useCallback(async (): Promise<Incident | null> => {
    if (!selectedIncident?.incident_id) return null;
    try {
      const fresh = await incidentsApi.getIncident(selectedIncident.incident_id);
      applySelectedIncident(fresh);
      return fresh;
    } catch {
      return null;
    }
  }, [applySelectedIncident, selectedIncident?.incident_id]);

  const selectedIncidentAttachmentCount = Array.isArray(selectedIncident?.evidence?.attachments)
    ? selectedIncident?.evidence?.attachments?.length || 0
    : 0;
  const selectedIncidentSnapshotFilename = String(selectedIncident?.evidence?.latest?.snapshot?.filename || '').trim();
  const selectedIncidentSnapshotStatus = String((selectedIncident as any)?.snapshot_status || '').trim().toUpperCase();
  const selectedIncidentClipFilename = String(selectedIncident?.evidence?.latest?.video?.filename || '').trim();
  const selectedIncidentClipStatus = String((selectedIncident as any)?.clip_status || '').trim().toUpperCase();
  const selectedIncidentClipUri = String((selectedIncident as any)?.clip_uri || '').trim();
  const selectedIncidentVersion = Number((selectedIncident as any)?.version || 0);
  const incidentDraftSyncKeyRef = useRef<string>('');
  const correctiveActionDraftTouchedRef = useRef(false);
  const [workflowFeedback, setWorkflowFeedback] = useState<{
    severity: 'success' | 'info' | 'warning';
    message: string;
  } | null>(null);
  const [workflowCorrectiveActions, setWorkflowCorrectiveActions] = useState<{
    active_actions: WorkflowCorrectiveAction[];
    completed_actions: WorkflowCorrectiveAction[];
    cancelled_actions: WorkflowCorrectiveAction[];
    evidence_summary: WorkflowCorrectiveActionEvidenceSummary | null;
  }>({
    active_actions: [],
    completed_actions: [],
    cancelled_actions: [],
    evidence_summary: null,
  });
  const [workflowImpactSummary, setWorkflowImpactSummary] = useState<WorkflowImpactSummary | null>(null);
  const [workflowClaimReadiness, setWorkflowClaimReadiness] = useState<WorkflowClaimReadinessResponse | null>(null);
  const [workflowFieldVerification, setWorkflowFieldVerification] = useState<FieldVerificationSummary | null>(null);
  const [fieldVerificationBusy, setFieldVerificationBusy] = useState(false);
  const [claimReadinessRefreshKey, setClaimReadinessRefreshKey] = useState(0);
  const canonicalWorkflowMode = groupedReviewMode;

  const clearWorkflowFeedback = useCallback(() => {
    setWorkflowFeedback(null);
  }, []);
  const bumpClaimReadinessRefresh = useCallback(() => {
    setClaimReadinessRefreshKey((current) => current + 1);
  }, []);

  const primaryWorkflowAction = useMemo(
    () => workflowCorrectiveActions.active_actions[0] || workflowCorrectiveActions.completed_actions[0] || null,
    [workflowCorrectiveActions.active_actions, workflowCorrectiveActions.completed_actions],
  );

  const refreshWorkflowCorrectiveSummary = useCallback(async () => {
    const workflowKey = String(workflowId || '').trim();
    if (!groupedReviewMode || !workflowKey) {
      setWorkflowCorrectiveActions({
        active_actions: [],
        completed_actions: [],
        cancelled_actions: [],
        evidence_summary: null,
      });
      setWorkflowImpactSummary(null);
      setWorkflowClaimReadiness(null);
      setWorkflowFieldVerification(null);
      return;
    }
    try {
      const [actionsResponse, impactResponse, claimReadinessResponse, fieldVerificationResponse] = await Promise.all([
        reportsApi.listWorkflowCorrectiveActions(workflowKey),
        reportsApi.getWorkflowImpactSummary(workflowKey),
        reportsApi.getWorkflowClaimReadiness(workflowKey, null),
        reportsApi.listWorkflowFieldVerification(workflowKey).catch(() => null),
      ]);
      const nextActiveActions = Array.isArray(actionsResponse?.active_actions) ? actionsResponse.active_actions : [];
      const nextCompletedActions = Array.isArray(actionsResponse?.completed_actions) ? actionsResponse.completed_actions : [];
      const nextCancelledActions = Array.isArray(actionsResponse?.cancelled_actions) ? actionsResponse.cancelled_actions : [];
      setWorkflowCorrectiveActions({
        active_actions: nextActiveActions,
        completed_actions: nextCompletedActions,
        cancelled_actions: nextCancelledActions,
        evidence_summary: actionsResponse?.evidence_summary || null,
      });
      if (!correctiveActionDraftTouchedRef.current) {
        const nextPrimaryAction = nextActiveActions[0] || nextCompletedActions[0] || nextCancelledActions[0] || null;
        setCorrectiveActionVerificationMethodDraftState(String(nextPrimaryAction?.verification_method || ''));
      }
      setWorkflowImpactSummary(impactResponse || null);
      setWorkflowClaimReadiness(claimReadinessResponse?.ok ? claimReadinessResponse : null);
      if (fieldVerificationResponse && fieldVerificationResponse.ok !== false) {
        setWorkflowFieldVerification(fieldVerificationResponse);
      } else {
        setWorkflowFieldVerification(emptyFieldVerificationSummary(workflowKey));
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to load workflow corrective-action summary');
    }
  }, [groupedReviewMode, setError, workflowId]);

  const handleAnswerFieldVerificationItem = useCallback(async (input: {
    item_key: string;
    disposition: string;
    note?: string;
    evidence_ref?: string;
  }) => {
    const workflowKey = String(workflowId || '').trim();
    if (!groupedReviewMode || !workflowKey) return;
    setFieldVerificationBusy(true);
    try {
      const response = await reportsApi.answerWorkflowFieldVerificationItem(workflowKey, {
        ...input,
        incident_id: String(selectedIncident?.incident_id || '').trim() || undefined,
      });
      if (response?.ok === false) {
        throw new Error(response.error || 'field_verification_answer_failed');
      }
      setWorkflowFieldVerification(response);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save field verification answer');
      throw e;
    } finally {
      setFieldVerificationBusy(false);
    }
  }, [groupedReviewMode, selectedIncident?.incident_id, setError, workflowId]);

  const mergeGroupedFollowupOwner = useCallback(
    (incident: Incident | null, owner: string): Incident | null => {
      if (!incident) return incident;
      const label = String(owner || '').trim();
      if (!label || ownerRoleIsUnassigned(label)) return incident;
      return {
        ...incident,
        followup_owner: label,
        followup_owner_display: label,
      } as Incident;
    },
    [],
  );

  const setCorrectiveActionTypeDraft = useCallback((value: string) => {
    correctiveActionDraftTouchedRef.current = true;
    setCorrectiveActionTypeDraftState(value);
  }, []);

  const setCorrectiveActionPriorityDraft = useCallback((value: string) => {
    correctiveActionDraftTouchedRef.current = true;
    setCorrectiveActionPriorityDraftState(value);
  }, []);

  const setCorrectiveActionDueDateDraft = useCallback((value: string) => {
    correctiveActionDraftTouchedRef.current = true;
    setCorrectiveActionDueDateDraftState(value);
  }, []);

  const setCorrectiveActionVerificationMethodDraft = useCallback((value: string) => {
    correctiveActionDraftTouchedRef.current = true;
    setCorrectiveActionVerificationMethodDraftState(value);
  }, []);

  useEffect(() => {
    if (!selectedIncident?.incident_id) {
      incidentDraftSyncKeyRef.current = '';
      correctiveActionDraftTouchedRef.current = false;
      setWorkflowFeedback(null);
      return;
    }
    const reviewMode = groupedReviewMode ? 'grouped' : 'single';
    const version = Number((selectedIncident as any).version || 1);
    const syncKey = `${selectedIncident.incident_id}:${reviewMode}:${version}`;
    if (incidentDraftSyncKeyRef.current === syncKey) return;
    incidentDraftSyncKeyRef.current = syncKey;

    const measurement = ((selectedIncident as any)?.envelope_metadata?.measurement || {}) as Record<string, any>;
    setNoteDraft('');
    setSeverityDraft((selectedIncident as any).severity || 'medium');
    setOpsStateDraft((String((selectedIncident as any).ops_state || '').trim().toLowerCase() as IncidentOpsState) || 'new');
    setOpsAssigneeDraft(
      groupedReviewMode
        ? readFollowupOwnerLabel(selectedIncident as any)
        : String((selectedIncident as any).assignee || ''),
    );
    setOpsTeamDraft(
      groupedReviewMode
        ? String((selectedIncident as any).followup_team || '')
        : String((selectedIncident as any).assignment_team || ''),
    );
    setOpsSlaTargetDraft(
      toLocalInputValue(
        groupedReviewMode
          ? (selectedIncident as any).followup_next_review_at
            || (selectedIncident as any).sla_target_at
            || ''
          : (selectedIncident as any).sla_target_at || '',
      ),
    );
    setMeasurementBaselineDraft(
      measurement?.baseline_count != null && Number.isFinite(Number(measurement.baseline_count))
        ? String(Number(measurement.baseline_count))
        : '',
    );
    setMeasurementFollowUpDraft(
      measurement?.follow_up_count != null && Number.isFinite(Number(measurement.follow_up_count))
        ? String(Number(measurement.follow_up_count))
        : '',
    );
    setMeasurementMeasuredAtDraft(
      toLocalInputValue(String(measurement?.action_effective_at || measurement?.measured_at || '')),
    );
    setMeasurementOutcomeStatusDraft(String(measurement?.outcome_status || ''));
    setMeasurementNoteDraft(String(measurement?.note || ''));
    setMeasurementImpactWaivedDraft(
      measurement?.impact_waived === true
      || readImpactMeasurementWaived(selectedIncident as any),
    );
    const normalizedWorkflowReviewDate = (() => {
      const raw = String(primaryWorkflowAction?.due_date || '').trim();
      if (!raw) return '';
      return toLocalDateInputValue(raw);
    })();
    const preserveCorrectiveDrafts = groupedReviewMode
      && correctiveActionDraftTouchedRef.current
      && (
        primaryWorkflowAction
          ? (
            String(opsAssigneeDraft || '').trim() !== String(primaryWorkflowAction.assignee || '').trim()
            || String(correctiveActionTypeDraft || '').trim().toLowerCase()
              !== String(primaryWorkflowAction.action_type || '').trim().toLowerCase()
            || String(correctiveActionPriorityDraft || '').trim().toLowerCase()
              !== String(primaryWorkflowAction.priority || '').trim().toLowerCase()
            || String(correctiveActionVerificationMethodDraft || '').trim().toLowerCase()
              !== String(primaryWorkflowAction.verification_method || '').trim().toLowerCase()
            || String(correctiveActionDueDateDraft || '').trim() !== normalizedWorkflowReviewDate
          )
          : canonicalWorkflowMode
            ? true
          : isGroupedCorrectiveActionPlanDraftDirty({
            incident: selectedIncident as any,
            followUpOwnerLabel: readFollowupOwnerLabel(selectedIncident as any),
            actionType: correctiveActionTypeDraft,
            priority: correctiveActionPriorityDraft,
            nextReviewDate: correctiveActionDueDateDraft,
          })
      );
    if (!preserveCorrectiveDrafts) {
      const legacyCorrectiveAction = readPersistedCorrectiveAction(
        selectedIncident as unknown as Record<string, unknown>,
      ) as Record<string, any>;
      const correctiveAction = primaryWorkflowAction
        ? {
          action_type: primaryWorkflowAction.action_type,
          due_at: primaryWorkflowAction.due_date,
        }
        : canonicalWorkflowMode
          ? {}
          : legacyCorrectiveAction;
      correctiveActionDraftTouchedRef.current = false;
      setCorrectiveActionTypeDraftState(String(correctiveAction.action_type || ''));
      setCorrectiveActionPriorityDraftState(
        primaryWorkflowAction
          ? String(primaryWorkflowAction.priority || '')
          : canonicalWorkflowMode
            ? ''
            : String(legacyCorrectiveAction.priority || ''),
      );
      setCorrectiveActionVerificationMethodDraftState(
        primaryWorkflowAction
          ? String(primaryWorkflowAction.verification_method || '')
          : canonicalWorkflowMode
            ? ''
            : '',
      );
      setCorrectiveActionDueDateDraftState(
        toLocalDateInputValue(String(correctiveAction.due_at || correctiveAction.due_date || '')),
      );
      const controlReviewAlias = readControlReviewPolicyAlias(
        selectedIncident,
        controlReviewPolicyAlias,
      ) || String((selectedIncident as any)?.risk_type || correctiveAction.action_type || '').trim();
      setCorrectiveActionControlReviewDraft(
        buildControlReviewDraft({
          alias: controlReviewAlias,
          persisted: readPersistedControlReview(legacyCorrectiveAction),
          workflowReview: readPersistedControlReview(primaryWorkflowAction as Record<string, unknown>),
        }),
      );
    }
  }, [
    correctiveActionDueDateDraft,
    correctiveActionPriorityDraft,
    correctiveActionTypeDraft,
    correctiveActionVerificationMethodDraft,
    canonicalWorkflowMode,
    groupedReviewMode,
    opsAssigneeDraft,
    primaryWorkflowAction,
    controlReviewPolicyAlias,
    selectedIncident,
    selectedIncident?.incident_id,
    selectedIncidentVersion,
  ]);

  useEffect(() => {
    if (!selectedIncident?.incident_id) {
      setWorkflowCorrectiveActions({
        active_actions: [],
        completed_actions: [],
        cancelled_actions: [],
        evidence_summary: null,
      });
      setWorkflowImpactSummary(null);
      return;
    }
    if (!groupedReviewMode || !String(workflowId || '').trim()) return;
    void refreshWorkflowCorrectiveSummary();
  }, [claimReadinessRefreshKey, groupedReviewMode, refreshWorkflowCorrectiveSummary, selectedIncident?.incident_id, workflowId]);

  useEffect(() => {
    if (!groupedReviewMode || !primaryWorkflowAction || correctiveActionDraftTouchedRef.current) return;
    const workflowActionType = String(primaryWorkflowAction.action_type || '').trim();
    const workflowPriority = String(primaryWorkflowAction.priority || '').trim();
    const workflowDueDate = String(primaryWorkflowAction.due_date || '').trim();
    if (workflowActionType) setCorrectiveActionTypeDraftState(workflowActionType);
    if (workflowPriority) setCorrectiveActionPriorityDraftState(workflowPriority);
    if (workflowDueDate) setCorrectiveActionDueDateDraftState(toLocalDateInputValue(workflowDueDate));
    if (!String(opsAssigneeDraft || '').trim() && String(primaryWorkflowAction.assignee || '').trim()) {
      setOpsAssigneeDraft(String(primaryWorkflowAction.assignee || '').trim());
    }
  }, [groupedReviewMode, opsAssigneeDraft, primaryWorkflowAction]);

  useEffect(() => {
    if (!viewDialogOpen || !selectedIncident?.incident_id) return;

    const attachmentCount = selectedIncidentAttachmentCount;
    const hasSnapshot = selectedIncidentSnapshotStatus === 'AVAILABLE' || Boolean(selectedIncidentSnapshotFilename);
    const hasClip =
      selectedIncidentClipStatus === 'AVAILABLE' ||
      Boolean(selectedIncidentClipFilename) ||
      Boolean(selectedIncidentClipUri);
    const clipStillPending = !hasClip && (!selectedIncidentClipStatus || selectedIncidentClipStatus === 'PENDING');
    if ((hasSnapshot || attachmentCount > 0) && !clipStillPending) return;

    let cancelled = false;
    const startAt = Date.now();
    const intervalMs = 5000;
    const maxMs = 2 * 60 * 1000;

    const tick = async () => {
      if (cancelled) return;
      await refreshSelectedIncident();
      if (Date.now() - startAt > maxMs) {
        try {
          window.clearInterval(timer);
        } catch {
          // ignore
        }
      }
    };

    void tick();
    const timer = window.setInterval(() => {
      void tick();
    }, intervalMs);
    return () => {
      cancelled = true;
      try {
        window.clearInterval(timer);
      } catch {
        // ignore
      }
    };
  }, [
    refreshSelectedIncident,
    selectedIncident?.incident_id,
    selectedIncidentAttachmentCount,
    selectedIncidentClipFilename,
    selectedIncidentClipStatus,
    selectedIncidentClipUri,
    selectedIncidentSnapshotFilename,
    selectedIncidentSnapshotStatus,
    viewDialogOpen,
  ]);

  const handleAcknowledge = useCallback(async () => {
    if (!selectedIncident?.incident_id) return;
    setDrawerBusy('ack');
    try {
      const fresh = await incidentsApi.patchStatus(
        selectedIncident.incident_id,
        { status: 'ACKED' as any },
        { expected_version: Number((selectedIncident as any).version || 1) },
      );
      applySelectedIncident(fresh);
      await fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to acknowledge incident');
    } finally {
      setDrawerBusy(null);
    }
  }, [applySelectedIncident, fetchData, selectedIncident, setError]);

  const handleMarkInReview = useCallback(async () => {
    if (!selectedIncident?.incident_id) return;
    setDrawerBusy('review');
    try {
      const fresh = await incidentsApi.patchStatus(
        selectedIncident.incident_id,
        { status: 'IN_REVIEW' as any },
        { expected_version: Number((selectedIncident as any).version || 1) },
      );
      applySelectedIncident(fresh);
      await fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to mark incident in review');
    } finally {
      setDrawerBusy(null);
    }
  }, [applySelectedIncident, fetchData, selectedIncident, setError]);

  const buildOpsPatchBody = useCallback(
    (opts?: { includeOpsState?: boolean; followupOwner?: string }) => {
      const slaTargetIso = toIsoFromLocalInput(opsSlaTargetDraft || '');
      const includeOpsState = opts?.includeOpsState === true;
      const opsStatePatch = includeOpsState ? { ops_state: opsStateDraft || 'new' } : {};
      const followupOwner = String(opts?.followupOwner ?? opsAssigneeDraft ?? '').trim() || null;
      if (groupedReviewMode) {
        return {
          ...opsStatePatch,
          followup_owner: followupOwner,
          followup_team: String(opsTeamDraft || '').trim() || null,
          followup_next_review_at: slaTargetIso || null,
        };
      }
      return {
        ...opsStatePatch,
        assignee: String(opsAssigneeDraft || '').trim() || null,
        assignment_team: String(opsTeamDraft || '').trim() || null,
        sla_target_at: slaTargetIso || null,
      };
    },
    [
      groupedReviewMode,
      opsAssigneeDraft,
      opsSlaTargetDraft,
      opsStateDraft,
      opsTeamDraft,
    ],
  );

  const persistActionSnapshot = useCallback(async (opts?: {
    ensureWorkOrder?: boolean;
    includeOpsState?: boolean;
    followupOwner?: string;
  }) => {
    if (!selectedIncident?.incident_id) return { incident: null as Incident | null, ensureRes: null as any };

    const patched = await incidentsApi.patchOps(
      selectedIncident.incident_id,
      buildOpsPatchBody({
        includeOpsState: opts?.includeOpsState,
        followupOwner: opts?.followupOwner,
      }),
      { expected_version: Number((selectedIncident as any).version || 1) },
    );
    const ownerLabel = String(opts?.followupOwner || '').trim();
    applySelectedIncident(
      groupedReviewMode && ownerLabel
        ? mergeGroupedFollowupOwner(patched, ownerLabel) || patched
        : patched,
    );

    let ensureRes: any = null;
    if (opts?.ensureWorkOrder) {
      ensureRes = await incidentsApi.ensureOdooTicket(selectedIncident.incident_id);
      if (!ensureRes?.ok) {
        return { incident: patched, ensureRes };
      }
      applySelectedIncident(patched);
    }

    void fetchData({ showLoading: false, skipPersistedRefresh: true });
    return { incident: patched, ensureRes };
  }, [
    applySelectedIncident,
    buildOpsPatchBody,
    fetchData,
    groupedReviewMode,
    mergeGroupedFollowupOwner,
    selectedIncident,
  ]);

  const syncValidationTaskFromLiveState = useCallback(async (input: {
    blockerType: GroupedBlockerSyncType;
    evidenceNote: string;
    resultPayload: Record<string, any>;
  }) => {
    const workflowKey = String(workflowId || '').trim();
    if (!workflowKey) return;
    const response = await reportsApi.getWorkflowClaimReadiness(workflowKey, null);
    const resolved = resolveOpenValidationTask(response, input.blockerType);
    if (!resolved?.taskId) return;
    const resultPayload = buildBlockerSyncResultPayload(
      input.blockerType,
      resolved.controlState,
      input.resultPayload,
      workflowImpactSummary,
    );
    await reportsApi.patchValidationTask(String(resolved.taskId), {
      status: 'completed',
      evidence_note: input.evidenceNote,
      result_payload: resultPayload,
    });
  }, [workflowId, workflowImpactSummary]);

  const patchGroupedMeasurementState = useCallback(async (input: {
    measurementPatch: Record<string, any>;
    blockerSync?: {
      blockerType: 'observation_coverage_reliability' | 'field_confirmation_of_zone_controls';
      evidenceNote: string;
      resultPayload: Record<string, any>;
    };
    successMessage: string;
    defaultError: string;
  }) => {
    if (!selectedIncident?.incident_id) return false;
    setDrawerBusy('measurement');
    try {
      const fresh = await incidentsApi.patchMeasurement(
        selectedIncident.incident_id,
        input.measurementPatch,
        { expected_version: Number((selectedIncident as any).version || 1) },
      );
      applySelectedIncident(fresh);
      await fetchData();
      if (input.blockerSync) {
        await syncValidationTaskFromLiveState(input.blockerSync);
      }
      await refreshWorkflowCorrectiveSummary();
      bumpClaimReadinessRefresh();
      setWorkflowFeedback({
        severity: 'success',
        message: input.successMessage,
      });
      setError(null);
      return true;
    } catch (e: any) {
      setWorkflowFeedback(null);
      setError(e?.response?.data?.error || e?.message || input.defaultError);
      return false;
    } finally {
      setDrawerBusy(null);
    }
  }, [
    applySelectedIncident,
    bumpClaimReadinessRefresh,
    fetchData,
    refreshWorkflowCorrectiveSummary,
    selectedIncident,
    setError,
    syncValidationTaskFromLiveState,
  ]);

  const resolveWorkOrderUrl = useCallback((incident: Incident | null, ensureRes: any): string => {
    const direct = String((incident as any)?.odoo_ticket_url || '').trim();
    if (direct) return direct;
    const ticketingUrl = String(ensureRes?.ticketing?.odoo?.url || '').trim();
    if (ticketingUrl) return ticketingUrl;
    const ticketId = (incident as any)?.odoo_ticket_id || ensureRes?.odoo_ticket_id;
    if (!ticketId) return '';
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    if (!origin) return '';
    return `${origin}/odoo-embed/web#id=${ticketId}&model=safety.incident&view_type=form`;
  }, []);

  const syncWorkflowActionOwner = useCallback(async (ownerValue?: string | null) => {
    const workflowKey = String(workflowId || '').trim();
    const actionId = String(primaryWorkflowAction?.action_id || '').trim();
    const owner = String(ownerValue || '').trim();
    if (!groupedReviewMode || !workflowKey || !actionId) return;
    await reportsApi.patchWorkflowCorrectiveAction(actionId, {
      assignee: owner || undefined,
    });
  }, [groupedReviewMode, primaryWorkflowAction?.action_id, workflowId]);

  const syncWorkflowActionStarted = useCallback(async (forceStarted = false) => {
    const actionId = String(primaryWorkflowAction?.action_id || '').trim();
    const currentStatus = String(primaryWorkflowAction?.status || '').trim().toLowerCase();
    const followUpState = String(opsStateDraft || '').trim().toLowerCase();
    const followUpStarted = forceStarted
      || ['acknowledged', 'in_progress', 'resolved', 'closed'].includes(followUpState);
    if (!groupedReviewMode || !actionId || !followUpStarted) return;
    if (['started', 'completed', 'validated', 'waived', 'cancelled'].includes(currentStatus)) return;
    await reportsApi.patchWorkflowCorrectiveAction(actionId, { status: 'started' });
  }, [
    groupedReviewMode,
    opsStateDraft,
    primaryWorkflowAction?.action_id,
    primaryWorkflowAction?.status,
  ]);

  const upsertWorkflowCorrectiveAction = useCallback(async () => {
    const workflowKey = String(workflowId || '').trim();
    if (!groupedReviewMode || !workflowKey) return null;
    const title = correctiveActionTypeDraft
      ? correctiveActionTypeLabel(correctiveActionTypeDraft)
      : 'Forklift / pedestrian corrective action';
    const payload = {
      action_type: String(correctiveActionTypeDraft || '').trim() || 'forklift_pedestrian_separation',
      title,
      description: String(primaryWorkflowAction?.description || '').trim() || undefined,
      priority: String(correctiveActionPriorityDraft || '').trim() || undefined,
      assignee: String(opsAssigneeDraft || '').trim() || undefined,
      verification_method: String(correctiveActionVerificationMethodDraft || '').trim() || undefined,
      due_date: toIsoFromDateInput(correctiveActionDueDateDraft || '')
        || toIsoFromLocalInput(correctiveActionDueDateDraft || '')
        || undefined,
      status: String(primaryWorkflowAction?.status || '').trim() || 'planned',
      safety_control_review: buildControlReviewPatch(correctiveActionControlReviewDraft),
    } as const;
    if (String(primaryWorkflowAction?.action_id || '').trim()) {
      return reportsApi.patchWorkflowCorrectiveAction(String(primaryWorkflowAction?.action_id || '').trim(), payload);
    }
    return reportsApi.createWorkflowCorrectiveAction(workflowKey, payload);
  }, [
    correctiveActionDueDateDraft,
    correctiveActionPriorityDraft,
    correctiveActionTypeDraft,
    correctiveActionVerificationMethodDraft,
    correctiveActionControlReviewDraft,
    groupedReviewMode,
    opsAssigneeDraft,
    primaryWorkflowAction?.action_id,
    primaryWorkflowAction?.description,
    primaryWorkflowAction?.status,
    workflowId,
  ]);

  const handleAddWorkflowEvidenceNote = useCallback(async (actionId: string, description: string) => {
    const nextActionId = String(actionId || primaryWorkflowAction?.action_id || '').trim();
    const note = String(description || '').trim();
    if (!groupedReviewMode || !nextActionId || !note) return;
    setDrawerBusy('ops-corrective');
    try {
      await reportsApi.addWorkflowCorrectiveActionEvidence(nextActionId, {
        evidence_type: 'note',
        description: note,
      });
      await refreshWorkflowCorrectiveSummary();
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save workflow evidence note');
    } finally {
      setDrawerBusy(null);
    }
  }, [groupedReviewMode, primaryWorkflowAction?.action_id, refreshWorkflowCorrectiveSummary, setError]);

  const handleUpdateWorkflowActionStatus = useCallback(async (actionId: string, status: 'completed' | 'validated') => {
    const nextActionId = String(actionId || primaryWorkflowAction?.action_id || '').trim();
    if (!groupedReviewMode || !nextActionId) return;
    setDrawerBusy('ops-corrective');
    try {
      await reportsApi.patchWorkflowCorrectiveAction(nextActionId, { status });
      await refreshWorkflowCorrectiveSummary();
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to update workflow corrective action');
    } finally {
      setDrawerBusy(null);
    }
  }, [groupedReviewMode, primaryWorkflowAction?.action_id, refreshWorkflowCorrectiveSummary, setError]);

  const handleEnsureWorkOrder = useCallback(async () => {
    if (!selectedIncident?.incident_id) return;
    setDrawerBusy('ensure');
    try {
      const { incident: fresh, ensureRes: res } = await persistActionSnapshot({ ensureWorkOrder: true });
      if (!res?.ok) {
        setWorkflowFeedback(null);
        setError((res as any)?.error || 'Failed to create work order');
        return;
      }
      await syncWorkflowActionStarted(true);
      await refreshWorkflowCorrectiveSummary();
      const refreshed = await refreshSelectedIncident();
      const ticketId = Number((res as any)?.odoo_ticket_id || 0) || null;
      const baseIncident = (refreshed || fresh || selectedIncident) as any;
      const effectiveIncident = ticketId
        ? ({
          ...baseIncident,
          odoo_ticket_id: baseIncident?.odoo_ticket_id || ticketId,
          odoo_ticket_state: String(baseIncident?.odoo_ticket_state || 'new'),
          safety_case: {
            ...(baseIncident?.safety_case || {}),
            corrective_action: {
              ...((baseIncident?.safety_case?.corrective_action) || {}),
              has_work_order: true,
              work_order_state: String(
                baseIncident?.safety_case?.corrective_action?.work_order_state
                || baseIncident?.odoo_ticket_state
                || 'new',
              ),
            },
            action_snapshot: {
              ...((baseIncident?.safety_case?.action_snapshot) || {}),
              has_work_order: true,
            },
          },
        } as Incident)
        : (refreshed || fresh);
      if (effectiveIncident) {
        applySelectedIncident(effectiveIncident);
      }
      setError(null);
      const workOrderUrl = resolveWorkOrderUrl(effectiveIncident, res);
      const hasLinkedTicket = Boolean((effectiveIncident as any)?.odoo_ticket_id || res?.odoo_ticket_id);

      if (workOrderUrl) {
        const openedWindow = window.open(workOrderUrl, '_blank', 'noopener,noreferrer');
        setWorkflowFeedback({
          severity: 'success',
          message: openedWindow
            ? `Odoo work order ${ticketId || ''} linked. A work-order tab was opened.`
            : `Odoo work order ${ticketId || ''} linked. Your browser blocked the Odoo tab, so open it from the sidebar or retry.`,
        });
        return;
      }

      if (hasLinkedTicket) {
        setWorkflowFeedback({
          severity: 'success',
          message: `Odoo work order ${ticketId || ''} linked. The browser URL was not returned yet, but you can open it from the sidebar or retry in a moment.`,
        });
        return;
      }
      setWorkflowFeedback(null);
      setError('Work order could not be created. Odoo may be unavailable on this site — check Odoo integration or try again later.');
    } catch (e: any) {
      setWorkflowFeedback(null);
      setError(e?.response?.data?.error || e?.message || 'Failed to create work order');
    } finally {
      setDrawerBusy(null);
    }
  }, [
    applySelectedIncident,
    persistActionSnapshot,
    refreshSelectedIncident,
    refreshWorkflowCorrectiveSummary,
    resolveWorkOrderUrl,
    selectedIncident,
    setError,
    setWorkflowFeedback,
    syncWorkflowActionStarted,
  ]);

  const handleSyncToOdoo = useCallback(async () => {
    if (!selectedIncident?.incident_id) return;
    setDrawerBusy('sync');
    try {
      const ensureRes = await incidentsApi.ensureOdooTicket(selectedIncident.incident_id);
      const freshAfterEnsure = await refreshSelectedIncident();
      const effectiveIncident = freshAfterEnsure || selectedIncident;
      const hasTicket = Boolean((effectiveIncident as any)?.odoo_ticket_id);
      if (!ensureRes?.ok || !hasTicket) {
        const reason = ensureRes?.error || 'odoo_unavailable_or_ticket_not_linked';
        setError(`Odoo not ready: ${reason}. Try again after Odoo is healthy, or use “Create Work Order” first.`);
        return;
      }

      await incidentsApi.updateOdooTicket(selectedIncident.incident_id, {
        severity: (effectiveIncident as any)?.severity || selectedIncident.severity,
        state: (effectiveIncident as any)?.odoo_ticket_state || selectedIncident.odoo_ticket_state || undefined,
      });
      await refreshSelectedIncident();
      await fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to sync to Odoo');
    } finally {
      setDrawerBusy(null);
    }
  }, [fetchData, refreshSelectedIncident, selectedIncident, setError]);

  const handleSaveNote = useCallback(async () => {
    if (!selectedIncident?.incident_id) return;
    setDrawerBusy('note');
    try {
      const res = await incidentsApi.addComment(selectedIncident.incident_id, noteDraft || '');
      if (res?.comment) {
        setIncidentComments((prev) => {
          const next = [res.comment as IncidentComment, ...(prev || [])];
          const seen = new Set<string>();
          return next.filter((item) => {
            const key = `${item.id}:${item.created_at || ''}`;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        });
      }
      if (res?.incident) applySelectedIncident(res.incident);
      setNoteDraft('');
      setIncidentCommentsError(null);
      await fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save comment');
    } finally {
      setDrawerBusy(null);
    }
  }, [
    applySelectedIncident,
    fetchData,
    noteDraft,
    selectedIncident?.incident_id,
    setError,
    setIncidentComments,
    setIncidentCommentsError,
  ]);

  const handleAddCommentShortcut = useCallback(() => {
    setViewDialogTab(0);
    window.setTimeout(() => {
      try {
        commentInputRef.current?.focus();
      } catch {
        // ignore
      }
    }, 0);
  }, [commentInputRef, setViewDialogTab]);

  const handleOverrideSeverity = useCallback(async (severity: IncidentSeverity) => {
    if (!selectedIncident?.incident_id) return;
    setDrawerBusy('severity');
    try {
      const fresh = await incidentsApi.putSeverityOverride(
        selectedIncident.incident_id,
        severity,
        { expected_version: Number((selectedIncident as any).version || 1) },
      );
      applySelectedIncident(fresh);
      await fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to override severity');
    } finally {
      setDrawerBusy(null);
    }
  }, [applySelectedIncident, fetchData, selectedIncident, setError]);

  const handleSaveOps = useCallback(async () => {
    if (!selectedIncident?.incident_id) return;
    setDrawerBusy('ops');
    try {
      await persistActionSnapshot({ includeOpsState: true });
      await syncWorkflowActionOwner(opsAssigneeDraft);
      await syncWorkflowActionStarted();
      await refreshWorkflowCorrectiveSummary();
      bumpClaimReadinessRefresh();
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save action workflow');
    } finally {
      setDrawerBusy(null);
    }
  }, [
    opsAssigneeDraft,
    bumpClaimReadinessRefresh,
    persistActionSnapshot,
    refreshWorkflowCorrectiveSummary,
    selectedIncident?.incident_id,
    setError,
    syncWorkflowActionStarted,
    syncWorkflowActionOwner,
  ]);

  /** Risk decision inline assign: patch follow-up owner only (no ops_state transition). */
  const handleSaveFollowupOwner = useCallback(async (ownerOverride?: string): Promise<boolean> => {
    if (!selectedIncident?.incident_id) return false;
    const owner = String(ownerOverride ?? opsAssigneeDraft ?? '').trim();
    if (!owner) {
      setError(`Enter an ${displaySafetyManagerLabel('followup_owner').toLowerCase()} before saving.`);
      return false;
    }
    setOpsAssigneeDraft(owner);
    setDrawerBusy('ops-owner');
    try {
      applySelectedIncident({
        ...(selectedIncident as Incident),
        followup_owner: owner,
        followup_owner_display: owner,
      } as Incident);
      const { incident: saved } = await persistActionSnapshot({ followupOwner: owner });
      if (saved) {
        applySelectedIncident(mergeGroupedFollowupOwner(saved, owner) || saved);
      }
      await syncWorkflowActionOwner(owner);
      await refreshWorkflowCorrectiveSummary();
      setWorkflowFeedback({
        severity: 'success',
        message: `Action owner saved (${owner}). Record or view the site control on the Safety Case.`,
      });
      setError(null);
      return true;
    } catch (e: any) {
      setWorkflowFeedback(null);
      const refreshed = await refreshSelectedIncident();
      const recoveredOwner = String(readFollowupOwnerLabel(refreshed as any) || '').trim();
      if (recoveredOwner && recoveredOwner === owner) {
        applySelectedIncident(mergeGroupedFollowupOwner(refreshed as Incident, owner) || refreshed);
        setWorkflowFeedback({
          severity: 'success',
          message: `Action owner saved (${owner}). Record or view the site control on the Safety Case.`,
        });
        setError(null);
        return true;
      }
      const apiError = e?.response?.data?.error;
      if (apiError === 'ownership_schema_missing') {
        setError('Action owner could not be saved: ownership fields are not available on this server yet.');
      } else if (apiError === 'version_conflict') {
        setError('This incident changed elsewhere. Close and reopen the review, then try again.');
      } else {
        setError(e?.response?.data?.detail || apiError || e?.message || 'Failed to save action owner');
      }
      return false;
    } finally {
      setDrawerBusy(null);
    }
  }, [
    applySelectedIncident,
    mergeGroupedFollowupOwner,
    opsAssigneeDraft,
    persistActionSnapshot,
    refreshWorkflowCorrectiveSummary,
    refreshSelectedIncident,
    selectedIncident,
    setError,
    setOpsAssigneeDraft,
    syncWorkflowActionOwner,
  ]);

  const handleSaveCoverageReviewAttestation = useCallback(async (input: {
    reviewed: boolean;
    evidenceReference?: string | null;
  }) => {
    if (!groupedReviewMode) return false;
    const evidenceReference = String(input.evidenceReference || '').trim() || null;
    const validationSync = input.reviewed
      ? buildCoverageReviewValidationSync({
        evidenceReference,
      })
      : null;
    return patchGroupedMeasurementState({
      measurementPatch: {
        coverage_review: {
          reviewed: input.reviewed,
          review_status: input.reviewed ? 'reviewed' : null,
          evidence_reference: evidenceReference,
        },
      },
      blockerSync: validationSync
        ? {
          blockerType: 'observation_coverage_reliability',
          evidenceNote: validationSync.evidenceNote,
          resultPayload: validationSync.resultPayload,
        }
        : undefined,
      successMessage: 'Coverage reliability review saved to the live workflow.',
      defaultError: 'Failed to save coverage reliability review.',
    });
  }, [groupedReviewMode, patchGroupedMeasurementState]);

  const handleSaveZoneControlsConfirmation = useCallback(async (input: {
    controlsPresent: boolean;
    controlsFunctioning: boolean;
    evidenceReference?: string | null;
  }) => {
    if (!groupedReviewMode) return false;
    const evidenceReference = String(input.evidenceReference || '').trim() || null;
    const fullyConfirmed = input.controlsPresent && input.controlsFunctioning;
    return patchGroupedMeasurementState({
      measurementPatch: {
        zone_controls_confirmation: {
          controls_present: input.controlsPresent,
          controls_functioning: input.controlsFunctioning,
          evidence_reference: evidenceReference,
        },
      },
      blockerSync: fullyConfirmed
        ? {
          blockerType: 'field_confirmation_of_zone_controls',
          evidenceNote: evidenceReference
            ? `Zone controls were confirmed in the live workflow. Evidence reference: ${evidenceReference}.`
            : 'Zone controls were confirmed in the live workflow.',
          resultPayload: {
            controls_present: true,
            controls_functioning: true,
            evidence_reference: evidenceReference,
            derived_from_live_workflow: true,
          },
        }
        : undefined,
      successMessage: 'Zone-control confirmation saved to the live workflow.',
      defaultError: 'Failed to save zone-control confirmation.',
    });
  }, [groupedReviewMode, patchGroupedMeasurementState]);

  const handleSaveMeasurement = useCallback(async () => {
    if (!selectedIncident?.incident_id) return;
    if (groupedReviewMode) {
      setDrawerBusy('measurement');
      try {
        const fresh = await incidentsApi.patchMeasurement(
          selectedIncident.incident_id,
          {
            measured_at: toIsoFromLocalInput(measurementMeasuredAtDraft || '') || null,
            note: String(measurementNoteDraft || '').trim() || null,
            impact_waived: measurementImpactWaivedDraft,
            waiver_reason: measurementImpactWaivedDraft
              ? (String(measurementNoteDraft || '').trim() || 'insufficient post-action observation window was available')
              : null,
            waiver_scope: measurementImpactWaivedDraft ? 'report_finalization' : null,
          },
          { expected_version: Number((selectedIncident as any).version || 1) },
        );
        applySelectedIncident(fresh);
        await fetchData();
        await refreshWorkflowCorrectiveSummary();
        bumpClaimReadinessRefresh();
        setError(null);
      } catch (e: any) {
        setError(e?.response?.data?.error || e?.message || 'Failed to save impact review');
      } finally {
        setDrawerBusy(null);
      }
      return;
    }
    const parseOptionalNumber = (value: string): number | null => {
      const text = String(value || '').trim();
      if (!text) return null;
      const parsed = Number(text);
      return Number.isFinite(parsed) ? parsed : NaN;
    };

    const baseline = parseOptionalNumber(measurementBaselineDraft);
    const followUp = parseOptionalNumber(measurementFollowUpDraft);
    if (Number.isNaN(baseline) || Number.isNaN(followUp)) {
      setError('Baseline and follow-up counts must be valid numbers.');
      return;
    }

    setDrawerBusy('measurement');
    try {
      const fresh = await incidentsApi.patchMeasurement(
        selectedIncident.incident_id,
        {
          baseline_count: baseline,
          follow_up_count: followUp,
          measured_at: toIsoFromLocalInput(measurementMeasuredAtDraft || '') || null,
          outcome_status: String(measurementOutcomeStatusDraft || '').trim() || null,
          note: String(measurementNoteDraft || '').trim() || null,
          impact_waived: measurementImpactWaivedDraft,
          waiver_reason: measurementImpactWaivedDraft
            ? (String(measurementNoteDraft || '').trim() || 'insufficient post-action observation window was available')
            : null,
          waiver_scope: measurementImpactWaivedDraft ? 'report_finalization' : null,
        },
        { expected_version: Number((selectedIncident as any).version || 1) },
      );
      applySelectedIncident(fresh);
      await fetchData();
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to save follow-up measurement');
    } finally {
      setDrawerBusy(null);
    }
  }, [
    applySelectedIncident,
    fetchData,
    groupedReviewMode,
    measurementBaselineDraft,
    measurementFollowUpDraft,
    measurementMeasuredAtDraft,
    measurementImpactWaivedDraft,
    measurementNoteDraft,
    measurementOutcomeStatusDraft,
    refreshWorkflowCorrectiveSummary,
    bumpClaimReadinessRefresh,
    selectedIncident,
    setError,
  ]);

  const handleSaveCorrectiveAction = useCallback(async () => {
    if (!selectedIncident?.incident_id) return;
    setDrawerBusy('ops-corrective');
    try {
      const workflowKey = String(workflowId || '').trim();
      if (groupedReviewMode && workflowKey) {
        await upsertWorkflowCorrectiveAction();
        await refreshWorkflowCorrectiveSummary();
        const verificationSync = buildVerificationMethodValidationSync({
          verificationMethod: correctiveActionVerificationMethodDraft,
        });
        if (verificationSync) {
          await syncValidationTaskFromLiveState({
            blockerType: 'verification_method',
            evidenceNote: verificationSync.evidenceNote,
            resultPayload: verificationSync.resultPayload,
          });
        }
        bumpClaimReadinessRefresh();
      } else if (groupedReviewMode) {
        setWorkflowFeedback(null);
        setError(
          'This grouped review is missing workflow context. Reopen it from the recurring risk workflow before saving the action plan.',
        );
        return;
      } else {
        const expectedVersion = Number((selectedIncident as any).version || 1);
        const ownerDraft = String(opsAssigneeDraft || '').trim();
        const persistedOwner = readFollowupOwnerLabel(selectedIncident as any);
        const correctivePayload: any = {
          action_type: String(correctiveActionTypeDraft || '').trim() || null,
          priority: String(correctiveActionPriorityDraft || '').trim() || null,
          due_at: toIsoFromDateInput(correctiveActionDueDateDraft || '')
            || toIsoFromLocalInput(correctiveActionDueDateDraft || '')
            || null,
          safety_control_review: buildControlReviewPatch(correctiveActionControlReviewDraft),
        };
        if (
          groupedReviewMode
          && ownerDraft
          && !ownerRoleIsUnassigned(ownerDraft)
          && ownerDraft !== persistedOwner
        ) {
          correctivePayload.followup_owner = ownerDraft;
        }
        const fresh = await incidentsApi.patchCorrectiveAction(
          selectedIncident.incident_id,
          correctivePayload,
          { expected_version: expectedVersion },
        );
        const mergedFresh = (
          groupedReviewMode
          && ownerDraft
          && !ownerRoleIsUnassigned(ownerDraft)
          && ownerRoleIsUnassigned(readFollowupOwnerLabel(fresh as any))
        )
          ? ({
            ...fresh,
            followup_owner: ownerDraft,
            followup_owner_display: ownerDraft,
          } as Incident)
          : fresh;
        applySelectedIncident(mergedFresh);
        void fetchData({ showLoading: false, skipPersistedRefresh: true });
      }
      setWorkflowFeedback({
        severity: 'success',
        message:
          'Corrective action plan saved on this review item. Track follow-up in Step 3.',
      });
      setError(null);
    } catch (e: any) {
      setWorkflowFeedback(null);
      await refreshSelectedIncident();
      setError(
        e?.response?.data?.detail
        || e?.response?.data?.error
        || e?.message
        || 'Failed to save corrective action',
      );
    } finally {
      setDrawerBusy(null);
    }
  }, [
    applySelectedIncident,
    correctiveActionDueDateDraft,
    correctiveActionPriorityDraft,
    correctiveActionTypeDraft,
    correctiveActionVerificationMethodDraft,
    correctiveActionControlReviewDraft,
    fetchData,
    groupedReviewMode,
    opsAssigneeDraft,
    bumpClaimReadinessRefresh,
    refreshWorkflowCorrectiveSummary,
    refreshSelectedIncident,
    selectedIncident,
    setError,
    syncValidationTaskFromLiveState,
    upsertWorkflowCorrectiveAction,
    workflowId,
  ]);

  const openResolveDialogWithOutcome = useCallback((outcome: IncidentReviewOutcome) => {
    setCloseReviewOutcomePreset(outcome);
    switch (outcome) {
      case 'false_positive':
        setCloseReason('FALSE_POSITIVE');
        break;
      case 'insufficient_evidence':
      case 'expected_activity':
      case 'not_actionable':
        setCloseReason('NOT_ACTIONABLE');
        break;
      case 'demo_or_test_activity':
        setCloseReason('TEST');
        break;
      case 'duplicate':
        setCloseReason('DUPLICATE');
        break;
      case 'true_event':
      default:
        setCloseReason('TRUE_POSITIVE');
        break;
    }
    if (!String(closeNote || '').trim() && String(noteDraft || '').trim()) {
      setCloseNote(String(noteDraft || '').trim());
    }
    setCloseDialogOpen(true);
  }, [closeNote, noteDraft]);

  const handleMarkFalsePositive = useCallback(() => {
    openResolveDialogWithOutcome('false_positive');
  }, [openResolveDialogWithOutcome]);

  const handleExportEvidence = useCallback(async () => {
    if (!selectedIncident?.incident_id) return;
    setDrawerBusy('export');
    try {
      const snapshotPick = bestSnapshotAttachment(selectedIncident);
      const snapshotName = String(snapshotPick?.filename || '').trim();
      const snapshotAvailable =
        Boolean(snapshotName) ||
        String((selectedIncident as any)?.snapshot_status || '').trim().toUpperCase() === 'AVAILABLE' ||
        Boolean((selectedIncident as any)?.snapshot_uri);
      if (snapshotAvailable) {
        const blob =
          snapshotPick?.filename && snapshotPick.preferFileEndpoint
            ? await fetchEvidenceFileBlob(selectedIncident.incident_id, snapshotPick.filename)
            : await fetchLatestSnapshotBlob(selectedIncident.incident_id);
        downloadBlob(blob, snapshotName || `${selectedIncident.incident_id}.jpg`);
        return;
      }
      const videoName = String((selectedIncident.evidence as any)?.latest?.video?.filename || '').trim();
      if (videoName) {
        const blob = await fetchLatestVideoBlob(selectedIncident.incident_id);
        downloadBlob(blob, videoName || `${selectedIncident.incident_id}.mp4`);
        return;
      }
      setViewDialogTab(0);
      setError('No exportable evidence is available for this incident yet.');
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to export evidence');
    } finally {
      setDrawerBusy(null);
    }
  }, [
    bestSnapshotAttachment,
    downloadBlob,
    fetchEvidenceFileBlob,
    fetchLatestSnapshotBlob,
    fetchLatestVideoBlob,
    selectedIncident,
    setError,
    setViewDialogTab,
  ]);

  const handleOpenThresholdTuning = useCallback(() => {
    setThresholdDialogOpen(true);
  }, []);

  const handleOpenPolicyEditor = useCallback(() => {
    const params = new URLSearchParams();
    const orgId = String(selectedIncident?.org_id || getScopedOrgId() || '').trim();
    const siteId = String(selectedIncident?.site_id || getScopedSiteId() || '').trim();
    const cameraId = String(selectedIncident?.camera_id || '').trim();
    const policyType = String(selectedIncident?.policy_type || (selectedIncident as any)?.triggered_by?.policy_type || '').trim();
    if (orgId) params.set('org', orgId);
    if (siteId) params.set('site', siteId);
    if (cameraId) params.set('camera_id', cameraId);
    if (policyType) params.set('type', policyType);
    setThresholdDialogOpen(false);
    navigate(`/policies${params.toString() ? `?${params.toString()}` : ''}`);
  }, [getScopedOrgId, getScopedSiteId, navigate, selectedIncident]);

  const handleCloseIncidentWith = useCallback(async (
    reason: string,
    note: string,
    tags: string[],
    reviewOutcome?: string,
  ) => {
    const incidentId = String(
      resolveTargetIncidentId || selectedIncident?.incident_id || '',
    ).trim();
    if (!incidentId) {
      const message = 'No representative evidence is selected for this review decision.';
      setError(message);
      throw new Error(message);
    }

    setDrawerBusy('close');
    try {
      const result = await persistIncidentReviewDecision({
        incidentId,
        selectedIncidentId: String(selectedIncident?.incident_id || '').trim(),
        selectedIncidentVersion: Number((selectedIncident as any)?.version || 1),
        groupedReviewMode,
        reason,
        note,
        tags,
        reviewOutcome,
        applySelectedIncident,
        refreshQueueQuietly: () => {
          // Do not block the resolve dialog on a full grouped-queue reload (can take minutes).
          void fetchData({ showLoading: false, skipPersistedRefresh: true });
        },
        refreshSelectedIncident,
      });
      if (!result.ok) {
        setError(result.message);
        throw new Error(result.message);
      }
      setCloseDialogOpen(false);
      setCloseReviewOutcomePreset(null);
      setError(null);
    } finally {
      setDrawerBusy(null);
    }
  }, [
    applySelectedIncident,
    fetchData,
    groupedReviewMode,
    refreshSelectedIncident,
    resolveTargetIncidentId,
    selectedIncident,
    setError,
  ]);

  const loadEdgeSuppressions = useCallback(async () => {
    const deviceId = String(selectedIncident?.device_id || '').trim();
    if (!deviceId) {
      setEdgeSuppressions([]);
      setSuppressionsError(null);
      setSuppressionsLoading(false);
      return;
    }
    setSuppressionsLoading(true);
    setSuppressionsError(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/suppressions`);
      const items = (res?.data?.suppressions || []) as any[];
      const nextItems = Array.isArray(items) ? items : [];
      setEdgeSuppressions(nextItems);
      setEdgeSuppressionsByDevice((prev) => ({ ...prev, [deviceId]: nextItems }));
    } catch (e: any) {
      setSuppressionsError(e?.response?.data?.error || e?.message || 'Failed to load suppressions');
    } finally {
      setSuppressionsLoading(false);
    }
  }, [selectedIncident?.device_id, setEdgeSuppressionsByDevice]);

  const loadPpeMissingZoneDiag = useCallback(async () => {
    const deviceId = String(selectedIncident?.device_id || '').trim();
    if (!deviceId) {
      setPpeMissingZoneDiag(null);
      setPpeMissingZoneDiagError(null);
      setPpeMissingZoneDiagLoading(false);
      return;
    }
    setPpeMissingZoneDiagLoading(true);
    setPpeMissingZoneDiagError(null);
    try {
      const res = await api.get(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/diagnostics/ppe_missing_zone`);
      setPpeMissingZoneDiag(res?.data || null);
    } catch (e: any) {
      setPpeMissingZoneDiagError(e?.response?.data?.error || e?.message || 'Failed to load PPE missing-zone diagnostics');
    } finally {
      setPpeMissingZoneDiagLoading(false);
    }
  }, [selectedIncident?.device_id]);

  const handleSuppressSimilarWith = useCallback(async (durationSec: number, reason: string) => {
    if (!selectedIncident?.incident_id) return;
    setDrawerBusy('suppress');
    try {
      await incidentsApi.suppressSimilar(selectedIncident.incident_id, {
        duration_seconds: durationSec,
        reason: reason || undefined,
        scope: {
          camera_id: selectedIncident.camera_id,
          zone_id: selectedIncident.zone_id,
          incident_type: selectedIncident.incident_type,
        },
      });
      setSuppressDialogOpen(false);
      if (selectedIncident.device_id) {
        await loadEdgeSuppressionsForDevices([selectedIncident.device_id]);
      }
      await fetchData();
      await refreshSelectedIncident();
      await loadEdgeSuppressions();
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to suppress similar incidents');
    } finally {
      setDrawerBusy(null);
    }
  }, [fetchData, loadEdgeSuppressions, loadEdgeSuppressionsForDevices, refreshSelectedIncident, selectedIncident, setError]);

  const handleApplyThresholdAdjustmentWith = useCallback(async (adjustment: Record<string, number>, autoDeploy: boolean) => {
    if (!selectedIncident?.incident_id) return;
    setDrawerBusy('threshold');
    try {
      const res = await incidentsApi.adjustThreshold(selectedIncident.incident_id, { adjustment });
      if (res?.incident) applySelectedIncident(res.incident);
      try {
        const activity = await incidentsApi.listActivity(selectedIncident.incident_id, { limit: 200 });
        setIncidentActivityItems(Array.isArray(activity?.items) ? activity.items : []);
      } catch {
        // best-effort
      }
      if (res?.policy?.site_id && res?.policy?.policy_id && autoDeploy) {
        await policiesApi.deployPolicy(String(res.policy.site_id), String(res.policy.policy_id));
      }
      setThresholdDialogOpen(false);
      await fetchData();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Failed to adjust threshold');
    } finally {
      setDrawerBusy(null);
    }
  }, [applySelectedIncident, fetchData, selectedIncident?.incident_id, setError, setIncidentActivityItems]);

  useEffect(() => {
    if (!viewDialogOpen || !selectedIncident?.incident_id) {
      setEdgeSuppressions([]);
      setSuppressionsError(null);
      setSuppressionsLoading(false);
      setPpeMissingZoneDiag(null);
      setPpeMissingZoneDiagError(null);
      setPpeMissingZoneDiagLoading(false);
      return;
    }
    void loadEdgeSuppressions();
    void loadPpeMissingZoneDiag();
  }, [loadEdgeSuppressions, loadPpeMissingZoneDiag, selectedIncident?.incident_id, viewDialogOpen]);

  const matchesSuppression = useCallback((suppression: any, row: Incident | null | undefined): boolean => {
    if (!suppression || !row) return false;
    const scope = suppression?.scope && typeof suppression.scope === 'object' ? suppression.scope : {};
    const incidentType = scope.incident_type || null;
    const zoneId = scope.zone_id || null;
    const cameraId = scope.camera_id || null;
    if (incidentType && String(incidentType) !== String(row.incident_type)) return false;
    if (zoneId && String(zoneId) !== String(row.zone_id)) return false;
    if (cameraId && String(cameraId) !== String(row.camera_id)) return false;
    return true;
  }, []);

  const isSuppressedRow = useCallback((row: any): boolean => {
    const deviceId = String(row?.device_id || '').trim();
    if (!deviceId) return false;
    const items = edgeSuppressionsByDevice[deviceId] || [];
    return items.some((suppression) => matchesSuppression(suppression, row));
  }, [edgeSuppressionsByDevice, matchesSuppression]);

  const deriveLifecycle = useCallback((row: any) => {
    if (isSuppressedRow(row)) return 'Suppressed';
    return deriveOperationalStatus(row as any);
  }, [deriveOperationalStatus, isSuppressedRow]);

  const matchingSuppressions = useMemo(() => {
    return (edgeSuppressions || []).filter((suppression) => matchesSuppression(suppression, selectedIncident));
  }, [edgeSuppressions, matchesSuppression, selectedIncident]);

  const handleClearSuppressionForIncident = useCallback(async () => {
    const row = selectedIncident;
    const deviceId = String(row?.device_id || '').trim();
    if (!row?.incident_id || !deviceId) return;
    setDrawerBusy('sync');
    try {
      const payload = {
        incident_type: row.incident_type || null,
        zone_id: row.zone_id || null,
        camera_id: row.camera_id || null,
      };
      await api.post(`/api/v1/pipeline/devices/${encodeURIComponent(deviceId)}/edge/suppressions/clear`, payload);
      await loadEdgeSuppressions();
      await loadEdgeSuppressionsForDevices([deviceId]);
    } catch (e: any) {
      setError(e?.response?.data?.error || e?.message || 'Failed to clear matching suppression');
    } finally {
      setDrawerBusy(null);
    }
  }, [loadEdgeSuppressions, loadEdgeSuppressionsForDevices, selectedIncident, setError]);

  return {
    drawerBusy,
    suppressionsLoading,
    suppressionsError,
    edgeSuppressions,
    edgeSuppressionsByDevice,
    suppressDialogOpen,
    setSuppressDialogOpen,
    closeDialogOpen,
    setCloseDialogOpen,
    closeReviewOutcomePreset,
    setCloseReviewOutcomePreset,
    openResolveDialogWithOutcome,
    closeReason,
    setCloseReason,
    closeNote,
    setCloseNote,
    thresholdDialogOpen,
    setThresholdDialogOpen,
    noteDraft,
    setNoteDraft,
    severityDraft,
    setSeverityDraft,
    opsStateDraft,
    setOpsStateDraft,
    opsAssigneeDraft,
    setOpsAssigneeDraft,
    opsTeamDraft,
    setOpsTeamDraft,
    opsSlaTargetDraft,
    setOpsSlaTargetDraft,
    measurementBaselineDraft,
    setMeasurementBaselineDraft,
    measurementFollowUpDraft,
    setMeasurementFollowUpDraft,
    measurementMeasuredAtDraft,
    setMeasurementMeasuredAtDraft,
    measurementOutcomeStatusDraft,
    setMeasurementOutcomeStatusDraft,
    measurementNoteDraft,
    setMeasurementNoteDraft,
    measurementImpactWaivedDraft,
    setMeasurementImpactWaivedDraft,
    correctiveActionTypeDraft,
    setCorrectiveActionTypeDraft,
    correctiveActionPriorityDraft,
    setCorrectiveActionPriorityDraft,
    correctiveActionDueDateDraft,
    setCorrectiveActionDueDateDraft,
    correctiveActionVerificationMethodDraft,
    setCorrectiveActionVerificationMethodDraft,
    correctiveActionControlReviewDraft,
    setCorrectiveActionControlReviewDraft,
    handleSaveCorrectiveAction,
    workflowId,
    workflowCorrectiveActions,
    workflowImpactSummary,
    workflowClaimReadiness,
    workflowFieldVerification,
    fieldVerificationBusy,
    handleAnswerFieldVerificationItem,
    refreshWorkflowCorrectiveSummary,
    handleAddWorkflowEvidenceNote,
    handleUpdateWorkflowActionStatus,
    ppeMissingZoneDiag,
    ppeMissingZoneDiagLoading,
    ppeMissingZoneDiagError,
    refreshSelectedIncident,
    handleAcknowledge,
    handleMarkInReview,
    handleEnsureWorkOrder,
    handleSyncToOdoo,
    handleSaveNote,
    handleAddCommentShortcut,
    handleOverrideSeverity,
    handleSaveOps,
    handleSaveFollowupOwner,
    handleSaveCoverageReviewAttestation,
    handleSaveZoneControlsConfirmation,
    handleSaveMeasurement,
    claimReadinessRefreshKey,
    handleMarkFalsePositive,
    handleExportEvidence,
    handleOpenThresholdTuning,
    handleOpenPolicyEditor,
    handleCloseIncidentWith,
    handleSuppressSimilarWith,
    handleApplyThresholdAdjustmentWith,
    loadEdgeSuppressions,
    loadPpeMissingZoneDiag,
    matchingSuppressions,
    deriveLifecycle,
    handleClearSuppressionForIncident,
    workflowFeedback,
    clearWorkflowFeedback,
  };
}
